import { useState, useCallback, useEffect } from 'react';
import { eventBus } from '@/utils/eventBus';
import AuthService from '@/services/amai/auth.service';
import { setUserInfo, getUserInfo } from '@/utils/auth';

// 全局状态管理
let globalShowPhoneModal = false;
let globalNeedPhoneVerification = false;

const setGlobalShowPhoneModal = (show: boolean) => {
  globalShowPhoneModal = show;
  // 触发事件通知所有Hook实例更新状态
  eventBus.emit('phoneModalStateChanged', { show, needVerification: globalNeedPhoneVerification });
};

const setGlobalNeedPhoneVerification = (need: boolean) => {
  globalNeedPhoneVerification = need;
  // 触发事件通知所有Hook实例更新状态
  eventBus.emit('phoneModalStateChanged', { show: globalShowPhoneModal, needVerification: need });
};

export const usePhoneVerification = () => {
  const [showPhoneModal, setShowPhoneModal] = useState(globalShowPhoneModal);
  const [needPhoneVerification, setNeedPhoneVerification] = useState(globalNeedPhoneVerification);

  // 监听全局状态变化
  useEffect(() => {
    const handleStateChange = ({
      show,
      needVerification,
    }: {
      show: boolean;
      needVerification: boolean;
    }) => {
      setShowPhoneModal(show);
      setNeedPhoneVerification(needVerification);
    };

    const unsubscribe = eventBus.on('phoneModalStateChanged', handleStateChange);
    return () => unsubscribe();
  }, []);

  // 检查手机号是否需要验证（包含*号）
  const checkPhoneVerification = useCallback(async (mobile?: string) => {
    // console.log('[usePhoneVerification] 检查手机号:', mobile);
    if (mobile && mobile.includes('*')) {
      // console.log('[usePhoneVerification] 检测到手机号包含*，需要补充完整手机号');
      setGlobalNeedPhoneVerification(true);
      setGlobalShowPhoneModal(true);
      return true;
    }
    // console.log('[usePhoneVerification] 手机号完整，不需要验证');
    setGlobalNeedPhoneVerification(false);
    setGlobalShowPhoneModal(false);
    return false;
  }, []);

  // 从本地存储检查手机号是否需要验证
  const checkLocalUserPhone = useCallback(async () => {
    try {
      const userInfo = getUserInfo();
      if (userInfo && userInfo.mobile) {
        // console.log('[usePhoneVerification] 从本地存储检查手机号:', userInfo.mobile);
        const result = await checkPhoneVerification(userInfo.mobile);
        // console.log('[usePhoneVerification] 检查结果:', result);
        return result;
      }
    } catch (error) {
      console.error('[usePhoneVerification] 从本地存储获取用户信息失败:', error);
    }
    return false;
  }, [checkPhoneVerification]);

  // 检查当前用户手机号是否需要验证（API方式，用于需要最新数据时）
  const checkCurrentUserPhone = useCallback(async () => {
    try {
      const userProfile = await AuthService.getUserProfile();
      if (userProfile.code === 0 && userProfile.data) {
        // console.log('[usePhoneVerification] 获取用户信息成功，检查手机号');
        return await checkPhoneVerification(userProfile.data.mobile);
      }
    } catch (error) {
      console.error('[usePhoneVerification] 获取用户信息失败:', error);
    }
    return false;
  }, [checkPhoneVerification]);

  // 处理手机号补充成功
  const handlePhoneSuccess = useCallback(async (userInfo: any) => {
    // console.log('[usePhoneVerification] 手机号补充成功:', userInfo);
    setGlobalShowPhoneModal(false);
    setGlobalNeedPhoneVerification(false);

    // 更新本地存储的用户信息
    if (userInfo) {
      setUserInfo(userInfo);
      // console.log('[usePhoneVerification] 已更新本地用户信息');

      // 发送用户信息更新事件，通知其他组件
      eventBus.emit('userInfoUpdated', userInfo);
      // console.log('[usePhoneVerification] 已发送用户信息更新事件');
    }

    // 手机号补充完成后，触发手机号验证完成权益检查事件
    setTimeout(() => {
      eventBus.emit('checkPhoneVerificationReward');
    }, 500);
  }, []);

  // 处理手机号弹窗关闭（用户点击关闭按钮）
  const handlePhoneCancel = useCallback(() => {
    // console.log('[usePhoneVerification] 用户关闭手机号弹窗');
    // 关闭弹窗，但保持验证状态，这样下次尝试使用功能时会再次弹出
    setGlobalShowPhoneModal(false);
    // 注意：不重置needPhoneVerification状态，这样下次检查时仍会触发弹窗
  }, []);

  return {
    showPhoneModal,
    needPhoneVerification,
    checkPhoneVerification,
    checkLocalUserPhone,
    checkCurrentUserPhone,
    handlePhoneSuccess,
    handlePhoneCancel,
  };
};
